/** Page imports live only on the application routing boundary.
 * Metadata users must import registry.ts, never this module.
 */
import { prepareCurrentReviewRoute, withPreparedReview } from '@/features/limit-up-review/bootstrap';
import type { ComponentType } from 'react';
import type { RoutePath } from './registry';
import { api } from '@/lib/api/client';
import { isStockCodeFormat, normalizeSymbol } from '@/lib/utils/symbol';

type Loader = () => Promise<{ default: ComponentType }>;

const stockPreparation = new Map<string, number>();
/** A real stock navigation starts bounded preparation while its code downloads.
 * Imports for another/currently hidden route never trigger work. The mounted
 * shared query still reads and reports actual progress, errors and completion. */
export function prepareCurrentStockRoute() {
  const match = window.location.pathname.match(/^\/stock\/([^/]+)$/);
  if (!match) return;
  let raw: string;
  try { raw = decodeURIComponent(match[1]); } catch { return; }
  if (!isStockCodeFormat(raw)) return;
  const symbol = normalizeSymbol(raw);
  const now = Date.now();
  const early = (window as Window & { __stockEarlyPreparation?: {
    symbol: string; startedAt: number; matchesAuth: () => boolean;
  } }).__stockEarlyPreparation;
  if (early?.symbol === symbol && now >= early.startedAt && now - early.startedAt < 20_000 && early.matchesAuth()) {
    stockPreparation.set(symbol, now);
    return;
  }
  if (now - (stockPreparation.get(symbol) ?? 0) < 20_000) return;
  stockPreparation.set(symbol, now);
  while (stockPreparation.size > 32) stockPreparation.delete(stockPreparation.keys().next().value!);
  (window as Window & { __stockRoutePreparationRequested?: string }).__stockRoutePreparationRequested = symbol;
  void api.post(`/charts/chan/${symbol}/prepare`, null, {
    params: { years: 2, timeframe: 'daily', include_related: true },
  }).catch(() => { /* The mounted query owns visible retry/error handling. */ });
}

export const ROUTE_LOADERS: Record<RoutePath, Loader> = {
  '/premarket-digest': () => import('@/features/premarket-digest'),
  '/policy-calendar': () => import('@/features/policy-calendar'),
  '/auction-radar': () => import('@/features/auction-radar'),
  '/youzi-workbench': () => import('@/features/youzi-workbench'),
  '/intraday-mainline': () => import('@/features/intraday-mainline'),
  '/intraday-watch': () => import('@/features/intraday-watch'),
  '/fund-flow': () => import('@/features/fund-flow'),
  '/drawing-desk': () => import('@/features/drawing-desk'),
  '/pattern-scan': () => import('@/features/pattern-scan'),
  '/mm-cycle': () => import('@/features/mm-cycle'),
  '/reversal-radar': () => import('@/features/reversal-radar'),
  '/reversal-review': () => import('@/features/reversal-review'),
  '/smart-picker-v2': () => import('@/features/smart-picker-v2'),
  '/leader-selection': () => import('@/features/leader-selection'),
  '/leader-library': () => import('@/features/leader-selection/library'),
  '/signals': () => import('@/features/signals-hub'),
  '/portfolio': () => import('@/features/portfolio'),
  '/sentinel-v2': () => import('@/features/sentinel-v2'),
  '/anomaly': () => import('@/features/anomaly'),
  '/emotion-ladder': () => import('@/features/emotion-ladder'),
  '/ma99-review': () => import('@/features/ma99-review'),
  '/lhb': () => import('@/features/lhb-desk'),
  '/heat-radar': () => import('@/features/heat-radar'),
  '/sector-scan': () => import('@/features/sector-scan'),
  '/sector-structure': () => import('@/features/sector-structure'),
  '/anomaly-timeline': () => import('@/features/anomaly-timeline'),
  '/limit-up-review': () => {
    const preparation = prepareCurrentReviewRoute();
    const page = import('@/features/limit-up-review');
    return Promise.all([page, preparation]).then(([module, result]) => ({ default: withPreparedReview(module.default, result) }));
  },
  '/recent-limit-ladder': () => import('@/features/recent-limit-ladder'),
  '/limit-up-pattern': () => import('@/features/limit-up-pattern'),
  '/rotation-desk': () => import('@/features/rotation-desk'),
  '/patri-research': () => import('@/features/patri-research'),
  '/trendline-system': () => import('@/features/trendline-system'),
  '/index-valuation': () => import('@/features/index-valuation'),
  '/stock-descriptions': () => import('@/features/stock-descriptions'),
  '/classifications': () => import("@/features/classification-catalog"),
  '/stock-vault': () => import('@/features/stock-vault'),
  '/news-lens': () => import('@/features/news-lens'),
  '/deep-equity': () => import('@/features/deep-equity'),
  '/domain-atlas': () => import('@/features/domain-atlas'),
  '/sector-wave-v2': () => import('@/features/sector-wave-v2'),
  '/edge': () => import('@/features/edge'),
  '/research-agent': () => import('@/features/research-agent'),
  '/paper-ledger': () => import('@/features/paper-ledger'),
  '/backtest-board': () => import('@/features/backtest-board'),
  '/signal-statistics': () => import('@/features/signal-statistics'),
  '/a-share-backtest': () => import('@/features/a-share-backtest'),
  '/strategy-scorecard': () => import('@/features/strategy-scorecard'),
  '/trading-system': () => import('@/features/trading-system'),
  '/chan-rsi': () => import('@/features/chan-rsi'),
  '/watchlist': () => import('@/features/watchlist'),
  '/data-center': () => import('@/features/data-center'),
  '/notify-center': () => import('@/features/notify-center'),
  '/settings/signals': () => import('@/features/user-settings'),
  '/ai-assets': () => import('@/features/ai-assets'),
  '/dashboard': () => import('@/features/dashboard'),
  '/stock/:symbol': () => { prepareCurrentStockRoute(); return import('@/features/stock-detail/StockDetailRoute'); },
  '/ai-history': () => import('@/features/ai-history'),
  '/admin/signal-economy': () => import('@/features/signal-economy'),
} satisfies Record<RoutePath, Loader>;
